import { useCallback, useEffect, useState } from 'react'
import {
  adminArchiveKpiMetric,
  adminCreateKpiMetric,
  adminDeleteKpiMetric,
  adminUpdateKpiMetric,
  listKpiMetrics,
  type KpiCadence,
  type KpiDirection,
  type KpiMetric,
  type KpiMetricDraft,
  type KpiValueType,
} from '../lib/kpiApi'
import { adminListEmployees, type AdminEmployee } from '../lib/adminApi'
import KpiGrid from './KpiGrid'
import DeleteButton from './DeleteButton'

interface Props {
  token: string
  isAdmin: boolean
  /**
   * Hide the metric-management controls ("+ New metric", "Show archived") so
   * the grid can be embedded somewhere that is about reading it, not
   * administering it — Meeting mode, for one.
   */
  compact?: boolean
}

interface Draft {
  name: string
  employeeId: string
  target: string
  direction: KpiDirection
  valueType: KpiValueType
  cadence: KpiCadence
}

const EMPTY: Draft = {
  name: '',
  employeeId: '',
  target: '',
  direction: 'higher_is_better',
  valueType: 'number',
  cadence: 'weekly',
}

export default function KpiScorecard({ token, isAdmin, compact = false }: Props) {
  const [metrics, setMetrics] = useState<KpiMetric[] | null>(null)
  // Loaded lazily — only an admin opening the form needs the roster, and
  // fetching it for every staff phone that opens this tab would be wasted
  // weight on shop wifi.
  const [people, setPeople] = useState<AdminEmployee[] | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [saving, setSaving] = useState(false)
  const [archiveBusyId, setArchiveBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const rows = await listKpiMetrics(token, showArchived)
      setMetrics(rows)
    } catch (err) {
      setMetrics([])
      setProblem(err instanceof Error ? err.message : String(err))
    }
  }, [token, showArchived])

  useEffect(() => {
    void load()
  }, [load])

  const loadPeople = useCallback(async () => {
    if (!isAdmin || people) return
    try {
      setPeople(await adminListEmployees(token))
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    }
  }, [token, isAdmin, people])

  const startNew = () => {
    void loadPeople()
    setDraft(EMPTY)
    setEditing('new')
    setProblem(null)
  }

  const startEdit = (metric: KpiMetric) => {
    void loadPeople()
    setDraft({
      name: metric.name,
      employeeId: metric.employee_id,
      target: String(metric.target),
      direction: metric.direction,
      valueType: metric.value_type,
      cadence: metric.cadence,
    })
    setEditing(metric.metric_id)
    setProblem(null)
  }

  // A yes/no metric has no target to set — "yes" is the only reading — so
  // the database fixes target/direction itself and the form never asks.
  const isBoolean = draft.valueType === 'boolean'

  const save = async () => {
    const target = Number(draft.target)
    if (
      draft.name.trim() === '' ||
      draft.employeeId === '' ||
      (!isBoolean && (draft.target.trim() === '' || Number.isNaN(target)))
    ) {
      setProblem('Give it a name, pick a person, and set a target.')
      return
    }
    setSaving(true)
    setProblem(null)
    const payload: KpiMetricDraft = {
      name: draft.name,
      employeeId: draft.employeeId,
      target: isBoolean ? 1 : target,
      direction: isBoolean ? 'higher_is_better' : draft.direction,
      valueType: draft.valueType,
      cadence: draft.cadence,
    }
    try {
      if (editing === 'new') {
        await adminCreateKpiMetric(token, payload)
      } else if (editing) {
        await adminUpdateKpiMetric(token, editing, payload)
      }
      setEditing(null)
      await load()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const archiveToggle = async (metric: KpiMetric) => {
    setArchiveBusyId(metric.metric_id)
    setProblem(null)
    try {
      await adminArchiveKpiMetric(token, metric.metric_id, !metric.archived)
      setEditing(null)
      await load()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setArchiveBusyId(null)
    }
  }

  // Permanent (0024). Errors land in the form's banner, which stays open.
  const deleteMetric = async (metric: KpiMetric) => {
    setArchiveBusyId(metric.metric_id)
    setProblem(null)
    try {
      await adminDeleteKpiMetric(token, metric.metric_id)
      setEditing(null)
      await load()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setArchiveBusyId(null)
    }
  }

  if (metrics === null) return <p className="muted">Loading…</p>

  const activePeople = (people ?? []).filter((p) => p.active)

  if (editing) {
    const editingMetric = editing === 'new' ? null : metrics.find((m) => m.metric_id === editing) ?? null
    const target = Number(draft.target)
    const valid =
      draft.name.trim() !== '' &&
      draft.employeeId !== '' &&
      (isBoolean || (draft.target.trim() !== '' && !Number.isNaN(target)))

    return (
      <section className="form">
        <h2 className="form__title">{editing === 'new' ? 'New metric' : 'Edit metric'}</h2>

        <label className="field">
          <span className="field__label">Metric</span>
          <input
            className="field__input"
            value={draft.name}
            autoFocus
            placeholder="e.g. Cars Serviced"
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </label>

        <label className="field">
          <span className="field__label">Assigned to</span>
          <select
            className="field__input"
            value={draft.employeeId}
            onChange={(e) => setDraft({ ...draft, employeeId: e.target.value })}
          >
            <option value="">Pick a person</option>
            {activePeople.map((p) => (
              <option key={p.employee_id} value={p.employee_id}>
                {p.full_name}
              </option>
            ))}
          </select>
        </label>

        <div className="field-row">
          <label className="field">
            <span className="field__label">Kind of value</span>
            <select
              className="field__input"
              value={draft.valueType}
              onChange={(e) => setDraft({ ...draft, valueType: e.target.value as KpiValueType })}
            >
              <option value="number">Number</option>
              <option value="percentage">Percentage</option>
              <option value="boolean">Yes / No</option>
            </select>
          </label>

          <label className="field">
            <span className="field__label">Logged</span>
            <select
              className="field__input"
              value={draft.cadence}
              onChange={(e) => setDraft({ ...draft, cadence: e.target.value as KpiCadence })}
            >
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
            </select>
          </label>
        </div>

        {!isBoolean && (
          <div className="field-row">
            <label className="field">
              <span className="field__label">Target</span>
              <input
                className="field__input"
                type="number"
                inputMode="decimal"
                step="any"
                value={draft.target}
                onChange={(e) => setDraft({ ...draft, target: e.target.value })}
              />
            </label>

            <label className="field">
              <span className="field__label">Direction</span>
              <select
                className="field__input"
                value={draft.direction}
                onChange={(e) => setDraft({ ...draft, direction: e.target.value as KpiDirection })}
              >
                <option value="higher_is_better">Higher is better</option>
                <option value="lower_is_better">Lower is better</option>
              </select>
            </label>
          </div>
        )}

        {isBoolean && (
          <p className="muted">A yes/no metric is always green on "Yes" — there's no target to set.</p>
        )}

        {problem && <p className="banner banner--tight">{problem}</p>}

        <div className="form__actions">
          {/* Archive lives here rather than on the grid row: the row is one
              tap (the name) and this form is where it leads. Archiving keeps
              every entry — the project-wide rule — it just leaves the grid. */}
          {editingMetric && (
            <button
              type="button"
              className="btn btn--quiet form__actions-start"
              disabled={saving || archiveBusyId === editingMetric.metric_id}
              onClick={() => void archiveToggle(editingMetric)}
            >
              {editingMetric.archived ? 'Restore' : 'Archive'}
            </button>
          )}
          {editingMetric && (
            <DeleteButton
              noun="metric"
              disabled={saving || archiveBusyId === editingMetric.metric_id}
              consequence={`“${editingMetric.name}” will be removed together with every number ever entered for it — its whole history, for everyone. This is the one delete that loses real records.`}
              onDelete={() => deleteMetric(editingMetric)}
            />
          )}
          <button type="button" className="btn btn--quiet" onClick={() => setEditing(null)}>
            Cancel
          </button>
          <button type="button" className="btn" disabled={saving || !valid} onClick={() => void save()}>
            {saving ? 'Saving…' : editing === 'new' ? 'Create metric' : 'Save changes'}
          </button>
        </div>
      </section>
    )
  }

  // One grid per cadence: the columns are periods, and a daily metric and a
  // weekly one cannot share a column. Each grid only renders when it has rows.
  const weekly = metrics.filter((m) => m.cadence === 'weekly')
  const daily = metrics.filter((m) => m.cadence === 'daily')

  return (
    <section className="kpi">
      {problem && <p className="banner">{problem}</p>}

      {isAdmin && !compact && (
        <>
          <button type="button" className="btn btn--spaced" onClick={startNew}>
            + New metric
          </button>
          <label className="check check--inline">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <span className="muted">Show archived</span>
          </label>
        </>
      )}

      {metrics.length === 0 && (
        <div className="panel panel--flat">
          <p className="muted">
            {isAdmin
              ? 'No metrics yet. Add one above to start tracking.'
              : 'No metrics have been set up yet.'}
          </p>
        </div>
      )}

      {weekly.length > 0 && (
        <div className="group">
          <h3 className="group__label">Weekly</h3>
          <KpiGrid
            metrics={weekly}
            cadence="weekly"
            token={token}
            isAdmin={isAdmin}
            onChanged={() => void load()}
            onEdit={isAdmin ? startEdit : undefined}
          />
        </div>
      )}

      {daily.length > 0 && (
        <div className="group">
          <h3 className="group__label">Daily</h3>
          <KpiGrid
            metrics={daily}
            cadence="daily"
            token={token}
            isAdmin={isAdmin}
            onChanged={() => void load()}
            onEdit={isAdmin ? startEdit : undefined}
          />
        </div>
      )}
    </section>
  )
}
